import React, { useState, useEffect } from 'react';
import './StaffPage.css';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

// const API_URL = 'http://localhost:3001/api';
const API_URL = 'https://med-bot.dev-alb.ru/api';

const StaffPage = ({ onBack, user }) => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [staffUser, setStaffUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews');
  
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [rejectedReviews, setRejectedReviews] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  const fetchReviews = async () => {
    setIsLoading(true);
    try {
      const pending = await api.getReviews(null, null, 'pending');
      const approved = await api.getReviews(null, null, 'approved');
      const rejected = await api.getReviews(null, null, 'rejected');
      
      setPendingReviews(pending);
      setApprovedReviews(approved);
      setRejectedReviews(rejected);
      
      setStats({
        pending: pending.length,
        approved: approved.length,
        rejected: rejected.length
      });
    } catch (error) {
      console.error('Ошибка загрузки отзывов:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    const savedStaff = localStorage.getItem('staffUser');
    
    if (token && savedStaff) {
      try {
        const staffData = JSON.parse(savedStaff);
        setStaffUser(staffData);
        setIsAuthenticated(true);
        fetchReviews();
      } catch (e) {
        console.error('Ошибка парсинга:', e);
      }
    }
  }, []);

  const handleLogin = async () => {
    if (!loginForm.username || !loginForm.password) {
      setLoginError('Заполните все поля');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: loginForm.username,
          password: loginForm.password
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Важно: в ответе приходит data.user, а не data.admin
        const userData = data.user;
        
        if (userData && (userData.role === 'moderator' || userData.role === 'admin')) {
          setIsAuthenticated(true);
          setStaffUser(userData);
          setLoginError('');
          localStorage.setItem('staffToken', 'logged_in');
          localStorage.setItem('staffUser', JSON.stringify(userData));
          await fetchReviews();
        } else {
          setLoginError('Доступ запрещен. Недостаточно прав.');
        }
      } else {
        setLoginError(data.error || 'Неверный логин или пароль');
      }
    } catch (error) {
      console.error('Ошибка входа:', error);
      setLoginError('Ошибка подключения к серверу');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staffToken');
    localStorage.removeItem('staffUser');
    setIsAuthenticated(false);
    setStaffUser(null);
    setPendingReviews([]);
    setApprovedReviews([]);
    setRejectedReviews([]);
  };

  const handleModerateReview = async (reviewId, status) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/reviews/${reviewId}/moderate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          status, 
          moderated_by: staffUser?.id || 1 
        })
      });
      
      if (response.ok) {
        await fetchReviews();
        alert(`✅ Отзыв ${status === 'approved' ? 'одобрен' : 'отклонен'}`);
      } else {
        const error = await response.json();
        alert(`❌ Ошибка: ${error.error}`);
      }
    } catch (error) {
      console.error('Ошибка модерации:', error);
      alert('❌ Ошибка при модерации отзыва');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('ru-RU');
  };

  const getReviewTarget = (review) => {
    if (review.doctor_id) {
      return `👨‍⚕️ Врач (ID: ${review.doctor_id})`;
    }
    if (review.service_id) {
      return `📋 Услуга (ID: ${review.service_id})`;
    }
    return '❓ Неизвестно';
  };

  if (!isAuthenticated) {
    return (
      <div className="staff-page">
        <div className="staff-header">
          <button className="back-btn-full" onClick={onBack}>
            <span className="btn-icon">←</span>
            <span className="btn-text">{t('Назад')}</span>
          </button>
        </div>
        
        <div className="staff-login-container">
          <div className="staff-login-card">
            <div className="staff-login-icon">👥</div>
            <h2>Вход для персонала</h2>
            <p className="staff-login-subtitle">Введите учетные данные сотрудника</p>
            
            <div className="staff-login-form">
              <input
                type="text"
                className="staff-login-input"
                placeholder="Логин"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                disabled={isLoading}
              />
              <input
                type="password"
                className="staff-login-input"
                placeholder="Пароль"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                disabled={isLoading}
              />
              {loginError && <div className="staff-login-error">{loginError}</div>}
              <button className="staff-login-btn" onClick={handleLogin} disabled={isLoading}>
                {isLoading ? 'Загрузка...' : 'Войти'}
              </button>
              <p className="staff-login-hint">Демо: moderator / moderator123</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-page">
      <div className="staff-header">
        <button className="back-btn-full" onClick={onBack}>
          <span className="btn-icon">←</span>
          <span className="btn-text">{t('Назад')}</span>
        </button>
        <div className="staff-user-info">
          <span className="staff-user-name">👤 {staffUser?.username || staffUser?.email || 'Модератор'}</span>
          <span className="staff-user-role">🎫 {staffUser?.role === 'moderator' ? 'Модератор' : staffUser?.role === 'admin' ? 'Администратор' : 'Сотрудник'}</span>
          <button className="staff-logout-btn" onClick={handleLogout}>
            🚪 Выйти
          </button>
        </div>
      </div>

      <div className="staff-content">
        <div className="staff-stats">
          <div className="staff-stat-card pending">
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">На модерации</div>
          </div>
          <div className="staff-stat-card approved">
            <div className="stat-value">{stats.approved}</div>
            <div className="stat-label">Одобрено</div>
          </div>
          <div className="staff-stat-card rejected">
            <div className="stat-value">{stats.rejected}</div>
            <div className="stat-label">Отклонено</div>
          </div>
        </div>

        <div className="staff-tabs">
          <button 
            className={`staff-tab ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            ⭐ На модерации ({stats.pending})
          </button>
          <button 
            className={`staff-tab ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            ✅ Одобренные ({stats.approved})
          </button>
          <button 
            className={`staff-tab ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => setActiveTab('rejected')}
          >
            ❌ Отклоненные ({stats.rejected})
          </button>
        </div>

        <div className="staff-reviews-list">
          {isLoading ? (
            <div className="loading-spinner">Загрузка...</div>
          ) : (
            <>
              {activeTab === 'reviews' && pendingReviews.length === 0 && (
                <div className="no-reviews">Нет отзывов на модерацию</div>
              )}
              {activeTab === 'approved' && approvedReviews.length === 0 && (
                <div className="no-reviews">Нет одобренных отзывов</div>
              )}
              {activeTab === 'rejected' && rejectedReviews.length === 0 && (
                <div className="no-reviews">Нет отклоненных отзывов</div>
              )}
              
              {activeTab === 'reviews' && pendingReviews.map((review) => (
                <div key={review.id} className="staff-review-card">
                  <div className="review-header">
                    <div className="review-user">
                      <span className="user-name">{review.first_name} {review.last_name}</span>
                      <span className="review-date">{formatDate(review.created_at)}</span>
                    </div>
                    <div className="review-rating">
                      {'⭐'.repeat(review.rating)}
                    </div>
                  </div>
                  <div className="review-target">
                    {getReviewTarget(review)}
                  </div>
                  {review.comment && (
                    <div className="review-comment">
                      <p>{review.comment}</p>
                    </div>
                  )}
                  <div className="review-actions">
                    <button 
                      className="review-approve-btn"
                      onClick={() => handleModerateReview(review.id, 'approved')}
                      disabled={isLoading}
                    >
                      ✅ Одобрить
                    </button>
                    <button 
                      className="review-reject-btn"
                      onClick={() => handleModerateReview(review.id, 'rejected')}
                      disabled={isLoading}
                    >
                      ❌ Отклонить
                    </button>
                  </div>
                </div>
              ))}

              {activeTab === 'approved' && approvedReviews.map((review) => (
                <div key={review.id} className="staff-review-card approved">
                  <div className="review-header">
                    <div className="review-user">
                      <span className="user-name">{review.first_name} {review.last_name}</span>
                      <span className="review-date">{formatDate(review.created_at)}</span>
                    </div>
                    <div className="review-rating">
                      {'⭐'.repeat(review.rating)}
                    </div>
                  </div>
                  <div className="review-target">
                    {getReviewTarget(review)}
                  </div>
                  {review.comment && (
                    <div className="review-comment">
                      <p>{review.comment}</p>
                    </div>
                  )}
                  <div className="review-moderated">
                    Модератор: {review.moderated_by} | {formatDate(review.moderated_at)}
                  </div>
                </div>
              ))}

              {activeTab === 'rejected' && rejectedReviews.map((review) => (
                <div key={review.id} className="staff-review-card rejected">
                  <div className="review-header">
                    <div className="review-user">
                      <span className="user-name">{review.first_name} {review.last_name}</span>
                      <span className="review-date">{formatDate(review.created_at)}</span>
                    </div>
                    <div className="review-rating">
                      {'⭐'.repeat(review.rating)}
                    </div>
                  </div>
                  <div className="review-target">
                    {getReviewTarget(review)}
                  </div>
                  {review.comment && (
                    <div className="review-comment">
                      <p>{review.comment}</p>
                    </div>
                  )}
                  <div className="review-moderated">
                    Отклонен модератором | {formatDate(review.moderated_at)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffPage;